import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaLine } from 'react-icons/fa';
import axios from '../api/axios';
import classNames from 'classnames';

interface InviteLinkResp {
  ref: string;
  url: string;
  clicks?: number | null;
  signups?: number | null;
  conversions?: number | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  isEmployee?: boolean;  // 社員の場合は友人/家族タブを表示
}

const CARD_W = 960;
const CARD_H = 540;
const CACHE_VERSION = 'v6'; // 家族カード修正によるキャッシュ破棄

const InviteDialog: React.FC<Props> = ({ open, onClose, isEmployee = false }) => {
  const { t } = useTranslation();

  // 社員モード時のタブ: 'friend' | 'family'
  const [activeTab, setActiveTab] = useState<'friend' | 'family'>('friend');

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [link, setLink] = useState<InviteLinkResp | null>(null);

  // 家族リンク
  const [familyLink, setFamilyLink] = useState<string | null>(null);
  const [familyRef, setFamilyRef] = useState<string | null>(null);
  const [familyLoading, setFamilyLoading] = useState(false);
  const [familyCopied, setFamilyCopied] = useState(false);
  const [familyCardUrl, setFamilyCardUrl] = useState<string | null>(null);

  const [copyMsg, setCopyMsg] = useState<string | null>(null);
  const [tipsMsg, setTipsMsg] = useState<string | null>(null);

  const [cardUrl, setCardUrl] = useState<string | null>(null);
  const [cardBusy, setCardBusy] = useState(false);

  const [shareMenuOpen, setShareMenuOpen] = useState(false);

  const canShare = useMemo(() => typeof navigator !== 'undefined' && !!(navigator as any).share, []);
  const canShareFile = useMemo(() => typeof navigator !== 'undefined' && !!(navigator as any).canShare, []);

  // ref トークンから現在のドメインでリンクを組み立て（ローカル/テストサーバー対応）
  // `:` はURLパラメータとして安全なため encodeURIComponent 後に戻す（可読性向上）
  const buildUrl = (ref: string) =>
    `${window.location.origin}/invite?ref=${encodeURIComponent(ref).replace(/%3A/g, ':')}`;

  // 日本語: 招待リンク取得（未作成なら作成→取得）
  const fetchOrCreateLink = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r1 = await axios.get<InviteLinkResp>('/user/referrals/my-link');
      setLink(r1.data);
      return r1.data;
    } catch (e: any) {
      if (e?.response?.status === 404) {
        try {
          await axios.post('/user/referrals/my-link');
          const r2 = await axios.get<InviteLinkResp>('/user/referrals/my-link');
          setLink(r2.data);
          return r2.data;
        } catch (e2: any) {
          const msg = e2?.response?.data?.detail || e2?.message || t('common.error') || 'エラーが発生しました。';
          setErr(String(msg));
          return null;
        }
      } else {
        const msg = e?.response?.data?.detail || e?.message || t('common.error') || 'エラーが発生しました。';
        setErr(String(msg));
        return null;
      }
    } finally {
      setLoading(false);
    }
  };

  // 家族リンク取得
  const fetchFamilyLink = async () => {
    if (familyLink) return;
    setFamilyLoading(true);
    try {
      const res = await axios.get<{ ref: string; url: string }>('/user/referrals/my-family-link');
      const url = buildUrl(res.data.ref);
      setFamilyRef(res.data.ref);
      setFamilyLink(url);
    } catch {
      setFamilyLink(null);
    } finally {
      setFamilyLoading(false);
    }
  };

  const handleCopyFamily = () => {
    if (!familyLink) return;
    navigator.clipboard.writeText(familyLink).then(() => {
      setFamilyCopied(true);
      setTimeout(() => setFamilyCopied(false), 2000);
    });
  };

  // タブ切り替え時に家族リンクを遅延取得 + カード自動生成
  useEffect(() => {
    if (isEmployee && activeTab === 'family') {
      fetchFamilyLink();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isEmployee]);

  // 家族リンク取得後：既存の genCard を流用（友人タブと同一レイアウト）
  useEffect(() => {
    if (!familyRef || !familyLink) return;
    const cached = loadCardFromCache(familyRef);
    if (cached) { setFamilyCardUrl(cached); return; }
    // genCard を familyLink のデータで実行し、結果をキャッシュから取得
    const fakeData: InviteLinkResp = { ref: familyRef, url: familyLink, clicks: null, signups: null, conversions: null };
    genCard(true, fakeData).then(() => {
      const c = loadCardFromCache(familyRef);
      if (c) setFamilyCardUrl(c);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyRef, familyLink]);

  // 日本語: ダイアログ表示時に自動生成（キャッシュ優先）
  useEffect(() => {
    if (!open) return;
    setCopyMsg(null);
    setTipsMsg(null);
    setCardUrl(null);
    setShareMenuOpen(false);
    if (isEmployee) setActiveTab('friend');

    (async () => {
      const info = await fetchOrCreateLink();
      if (!info) return;
      const cached = loadCardFromCache(info.ref);
      if (cached) {
        setCardUrl(cached);
      } else {
        await genCard(true, info);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ========================
  // 日本語: キャッシュ
  // ========================
  const cacheKey = (ref: string) => `invite_card_${CACHE_VERSION}:${ref}`;
  const loadCardFromCache = (ref?: string) => {
    try {
      if (!ref) return null;
      const v = localStorage.getItem(cacheKey(ref));
      return v || null;
    } catch {
      return null;
    }
  };
  const saveCardToCache = (ref: string, dataUrl: string) => {
    try {
      localStorage.setItem(cacheKey(ref), dataUrl);
    } catch { }
  };
  const clearCardCache = (ref?: string) => {
    try {
      if (!ref) return;
      localStorage.removeItem(cacheKey(ref));
    } catch { }
  };

  // ========================
  // 日本語: 共有/コピー
  // ========================
  const onCopy = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(buildUrl(link.ref));
      setCopyMsg(t('invite.copied') || 'コピーしました');
      setTimeout(() => setCopyMsg(null), 2000);
    } catch {
      setCopyMsg(t('invite.copy_failed') || 'コピーに失敗しました');
      setTimeout(() => setCopyMsg(null), 2500);
    }
  };

  const openShareMenuOrNative = async (text?: string) => {
    if (!link?.url) return;
    try {
      if (canShare) {
        await (navigator as any).share({
          title: t('invite.share_title') || '友達紹介',
          text: text || (t('invite.share_text') || 'このリンクから登録すると特典がもらえます'),
          url: buildUrl(link.ref),
        });
        return;
      }
    } catch { }
    setShareMenuOpen((v) => !v);
  };

  // ========================
  // 日本語: 画像合成
  // ========================
  const loadImage = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  // 日本語: 明示改行(\n)対応の折返し描画
  const drawTextWrap = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number
  ) => {
    const paragraphs = (text || '').split(/\n/);
    for (const para of paragraphs) {
      const words = para.split(/(\s+)/);
      let line = '';
      for (let i = 0; i < words.length; i++) {
        const test = line + words[i];
        const w = ctx.measureText(test).width;
        if (w > maxWidth && line !== '') {
          ctx.fillText(line, x, y);
          line = words[i].trimStart();
          y += lineHeight;
        } else {
          line = test;
        }
      }
      if (line) ctx.fillText(line, x, y);
      y += lineHeight;
    }
    return y;
  };

  // 日本語: 招待カード生成（ブランド行 + 徽章行 + 本文 + 右側QR）
  const genCard = async (auto = false, linkData?: InviteLinkResp) => {
    const targetLink = linkData || link;
    // family カード生成時（linkData あり）は cardBusy を無視
    if (!targetLink?.url || (cardBusy && !linkData)) return;
    setCardBusy(true);
    setErr(null);
    try {
      const { toDataURL } = await import('qrcode');
      const qrDataUrl = await toDataURL(targetLink.url, { width: 380, margin: 2 });

      const canvas = document.createElement('canvas');
      canvas.width = CARD_W;
      canvas.height = CARD_H;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas-2d-unavailable');

      // 背景
      const g = ctx.createLinearGradient(0, 0, CARD_W, CARD_H);
      g.addColorStop(0, '#ffffff');
      g.addColorStop(1, '#f3f8ff');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, CARD_W, CARD_H);

      const PAD = 48;

      // 左パネル
      const panelX = PAD;
      const panelY = PAD;
      const panelW = CARD_W - PAD * 2 - 420;
      const panelH = CARD_H - PAD * 2;
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = 'rgba(15,23,42,0.06)';
      ctx.lineWidth = 1;
      const r = 24;
      ctx.beginPath();
      ctx.moveTo(panelX + r, panelY);
      ctx.arcTo(panelX + panelW, panelY, panelX + panelW, panelY + panelH, r);
      ctx.arcTo(panelX + panelW, panelY + panelH, panelX, panelY + panelH, r);
      ctx.arcTo(panelX, panelY + panelH, panelX, panelY, r);
      ctx.arcTo(panelX, panelY, panelX + panelW, panelY, r);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // ロゴ
      const logoSrc = `${process.env.PUBLIC_URL || ''}/images/melodis-icon.png`;
      try {
        const logo = await loadImage(logoSrc);
        const LOGO_H = 64;
        const ar = (logo.width || 1) / (logo.height || 1);
        const logoW = LOGO_H * ar;
        ctx.drawImage(logo, panelX + 24, panelY + 22, logoW, LOGO_H);
      } catch { }

      // ブランド行（斜体）
      let titleX = panelX + 24;
      let titleY = panelY + 24 + 70;
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#0f172a';
      ctx.font = 'italic 46px system-ui, -apple-system, Segoe UI, Roboto, Noto Sans, Helvetica, Arial';
      const brand = 'Melodis Clinic';
      ctx.fillText(brand, titleX, titleY);

      // 徽章行（青いピル・白文字）※ i18n
      const badgeTxt = (t('invite.badge') as string) || '招待特典';
      const badgePadX = 16;
      const badgePadY = 8;
      const badgeGapTop = 14;
      ctx.font = 'bold 34px system-ui, -apple-system, Segoe UI, Roboto, Noto Sans, Helvetica, Arial';
      const badgeW = ctx.measureText(badgeTxt).width + badgePadX * 2;
      const badgeH = 34 + badgePadY * 2;
      const badgeX = titleX;
      const badgeY = titleY + 46 + badgeGapTop; // ブランド行の下
      const badgeR = 18;

      // ピル背景
      ctx.fillStyle = '#2563eb';
      ctx.beginPath();
      ctx.moveTo(badgeX + badgeR, badgeY);
      ctx.arcTo(badgeX + badgeW, badgeY, badgeX + badgeW, badgeY + badgeH, badgeR);
      ctx.arcTo(badgeX + badgeW, badgeY + badgeH, badgeX, badgeY + badgeH, badgeR);
      ctx.arcTo(badgeX, badgeY + badgeH, badgeX, badgeY, badgeR);
      ctx.arcTo(badgeX, badgeY, badgeX + badgeW, badgeY, badgeR);
      ctx.closePath();
      ctx.fill();

      // ピル文字
      ctx.fillStyle = '#ffffff';
      ctx.fillText(badgeTxt, badgeX + badgePadX, badgeY + badgePadY);

      // 説明文（徽章の下から）
      const bodyTop = badgeY + badgeH + 18;
      ctx.font = '28px system-ui, -apple-system, Segoe UI, Roboto, Noto Sans, Helvetica, Arial';
      ctx.fillStyle = '#334155';
      const body =
        (t('invite.card_body') as string) ||
        'このQRコードから登録すると、\n招待特典がもらえます。\nご友人・ご家族にもぜひ。';
      drawTextWrap(ctx, body, panelX + 24, bodyTop, panelW - 48, 38);

      // 右QR
      const qrImg = await loadImage(qrDataUrl);
      const qrSize = 380;
      const qrX = CARD_W - PAD - qrSize;
      const qrY = PAD + 10;
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

      // QRヒント（中央寄せ・i18n）
      ctx.font = '20px system-ui';
      ctx.fillStyle = '#64748b';
      const hint = (t('invite.qr_hint') as string) || 'スマホでQRをスキャンして登録';
      const hintW = ctx.measureText(hint).width;
      const hintX = qrX + (qrSize - hintW) / 2;
      ctx.fillText(hint, hintX, qrY + qrSize + 20);

      const url = canvas.toDataURL('image/png');
      setCardUrl(url);
      if (targetLink.ref) saveCardToCache(targetLink.ref, url);
      if (!auto) setShareMenuOpen(false);
    } catch (e: any) {
      setErr(t('invite.card_failed') || '招待カードの生成に失敗しました。');
    } finally {
      setCardBusy(false);
    }
  };

  // 日本語: カード共有（対応環境はファイル共有、非対応時はメニュー）
  const shareCard = async () => {
    if (!cardUrl || !link?.url) return;
    try {
      const res = await fetch(cardUrl);
      const blob = await res.blob();
      const file = new File([blob], 'melodis_invite.png', { type: 'image/png' });

      if (canShare && canShareFile && (navigator as any).canShare({ files: [file] })) {
        await (navigator as any).share({
          title: t('invite.share_title') || '友達紹介',
          text: t('invite.share_text') || 'このリンクから登録すると特典がもらえます',
          files: [file],
        });
        return;
      }
    } catch { }
    setShareMenuOpen((v) => !v);
  };

  // 日本語: 兜底共有リンク（WeChat はガイド表示）
  const socialLinks = useMemo(() => {
    const url = encodeURIComponent(link?.url || '');
    const text = encodeURIComponent((t('invite.share_text') as string) || 'このリンクから登録すると特典がもらえます');
    return {
      x: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      line: `https://social-plugins.line.me/lineit/share?url=${url}`,
      fb: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      wa: `https://api.whatsapp.com/send?text=${text}%20${url}`,
    };
  }, [link?.url, t]);

  const familySocialLinks = useMemo(() => {
    const url = encodeURIComponent(familyLink || '');
    const text = encodeURIComponent((t('invite.share_text') as string) || 'このリンクから登録すると特典がもらえます');
    return {
      line: `https://social-plugins.line.me/lineit/share?url=${url}`,
    };
  }, [familyLink, t]);

  const [familyShareOpen, setFamilyShareOpen] = useState(false);

  const openFamilyShare = async () => {
    if (familyLink && canShare) {
      try {
        await (navigator as any).share({ url: familyLink, text: t('invite.share_text') || 'このリンクから登録すると特典がもらえます' });
        return;
      } catch { }
    }
    setFamilyShareOpen(v => !v);
  };

  const onWeChatGuide = () => {
    setTipsMsg(
      t('invite.wechat_tips') ||
      'WeChatではブラウザ共有が利用できません。カードをダウンロードして、WeChatで画像を送信してください。'
    );
    setTimeout(() => setTipsMsg(null), 4000);
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black/50 backdrop-blur-sm">
      <div className="flex min-h-full items-start sm:items-center justify-center p-4 pt-4 sm:pt-4">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl mt-16 sm:mt-0 overflow-hidden">

        {/* ヘッダー */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-gray-900">
              {isEmployee ? (t('invite.title_employee') || '社員紹介') : (t('invite.title') || '友達紹介')}
            </h2>
            {/* 社員モード: 友人/家族タブ */}
            {isEmployee && (
              <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setActiveTab('friend')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    activeTab === 'friend' ? 'bg-white text-coral shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('invite.tab_friend') || '友人'}
                </button>
                <button
                  onClick={() => setActiveTab('family')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    activeTab === 'family' ? 'bg-white text-coral shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('invite.tab_family') || '家族'}
                </button>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="p-6">

        {/* 家族紹介タブ */}
        {isEmployee && activeTab === 'family' ? (
          <div>
            {familyLoading ? (
              <p className="text-sm text-gray-600">{t('common.loading') || '読み込み中...'}</p>
            ) : familyLink ? (
              <>
                <div className="mb-5">
                  <label className="block text-sm text-gray-700 mb-1">{t('invite.your_link') || 'あなたの紹介リンク'}</label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input type="text" value={familyLink} readOnly className="flex-1 border rounded-lg px-3 py-2 outline-none select-all" />
                    <div className="flex gap-2">
                      <button onClick={handleCopyFamily} className="px-4 py-2 rounded-lg text-white bg-coral hover:opacity-90">
                        {familyCopied ? (t('invite.copied') || 'コピー済み') : (t('invite.copy') || 'コピー')}
                      </button>
                      <button onClick={openFamilyShare} className="px-4 py-2 rounded-lg border hover:bg-gray-100">
                        {t('invite.share') || '共有'}
                      </button>
                    </div>
                  </div>
                  {familyShareOpen && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                      <a className="px-4 py-2 rounded-xl bg-[#06C755] text-white flex items-center gap-2 shadow-lg shadow-green-500/20 transition-transform active:scale-95" href={familySocialLinks.line} target="_blank" rel="noreferrer">
                        <FaLine size={18} />LINEで送る
                      </a>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">{t('invite.family_desc') || 'ご家族をご招待いただくと、家族割引が適用されます。'}</p>
                </div>
                {/* 家族用QRカード（友人タブと同じ構成） */}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold">{t('invite.card_title') || '招待カード'}</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setFamilyCardUrl(null); if (familyRef && familyLink) { const fakeData: InviteLinkResp = { ref: familyRef, url: familyLink, clicks: null, signups: null, conversions: null }; genCard(false, fakeData).then(() => { const c = loadCardFromCache(familyRef); if (c) setFamilyCardUrl(c); }); } }}
                      className={classNames('px-3 py-1.5 rounded-lg text-sm border hover:bg-gray-100')}
                    >
                      {t('invite.card_regenerate') || '再生成'}
                    </button>
                    <a
                      href={familyCardUrl || '#'}
                      download="melodis_family_invite.png"
                      className={classNames('px-3 py-1.5 rounded-lg text-sm', familyCardUrl ? 'border hover:bg-gray-100' : 'border bg-gray-100 text-gray-400 cursor-not-allowed pointer-events-none')}
                    >
                      {t('invite.card_download') || 'ダウンロード'}
                    </a>
                    <button
                      onClick={openFamilyShare}
                      disabled={!familyCardUrl}
                      className={classNames('px-3 py-1.5 rounded-lg text-sm', familyCardUrl ? 'border hover:bg-gray-100' : 'border bg-gray-100 text-gray-400 cursor-not-allowed')}
                    >
                      {t('invite.share') || '共有'}
                    </button>
                  </div>
                </div>
                <div className="border rounded-xl p-4 flex items-center justify-center min-h-[220px]">
                  {familyCardUrl ? (
                    <img src={familyCardUrl} alt="家族招待カード" className="rounded-xl border max-h-[360px] w-auto" />
                  ) : (
                    <p className="text-sm text-gray-600">{t('invite.card_hint') || '初回は自動生成されます。更新したい場合は「再生成」を押してください。'}</p>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-red-400">リンクの取得に失敗しました</p>
            )}
          </div>
        ) : loading ? (
          <p className="text-sm text-gray-600">{t('common.loading') || '読み込み中...'}</p>
        ) : err ? (
          <p className="text-sm text-red-600">{err}</p>
        ) : !link ? (
          <p className="text-sm text-gray-600">{t('invite.no_link') || 'リンクが取得できませんでした。'}</p>
        ) : (
          <>
            {/* 日本語: リンク（明文はここだけ、カードには表示しない） */}
            <div className="mb-5">
              <label className="block text-sm text-gray-700 mb-1">
                {t('invite.your_link') || 'あなたの紹介リンク'}
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={buildUrl(link.ref)}
                  readOnly
                  className="flex-1 border rounded-lg px-3 py-2 outline-none select-all"
                />
                <div className="flex gap-2">
                  <button
                    onClick={onCopy}
                    className="px-4 py-2 rounded-lg text-white bg-coral hover:opacity-90"
                  >
                    {t('invite.copy') || 'コピー'}
                  </button>
                  <button
                    onClick={() => openShareMenuOrNative()}
                    className="px-4 py-2 rounded-lg border hover:bg-gray-100"
                  >
                    {t('invite.share') || '共有'}
                  </button>
                </div>
              </div>
              {copyMsg && <p className="text-sm mt-2 text-gray-700">{copyMsg}</p>}

              {shareMenuOpen && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                  <a className="px-4 py-2 rounded-xl bg-[#06C755] text-white flex items-center gap-2 shadow-lg shadow-green-500/20 transition-transform active:scale-95" href={socialLinks.line} target="_blank" rel="noreferrer">
                    <FaLine size={18} />
                    LINEで送る
                  </a>
                </div>
              )}

              {tipsMsg && <p className="text-xs text-amber-700 mt-2">{tipsMsg}</p>}

              <p className="text-xs text-gray-500 mt-2">
                {t('invite.tip') || '友だちにこのリンクを送ってください。登録後に特典が付与されます。'}
              </p>
            </div>

            {(link.clicks ?? null) !== null ||
              (link.signups ?? null) !== null ||
              (link.conversions ?? null) !== null ? (
              <div className="grid grid-cols-3 gap-3 mb-6">
                {(link.clicks ?? null) !== null && (
                  <div className="border rounded-xl p-4 text-center">
                    <div className="text-xs text-gray-500">{t('invite.stat_clicks') || 'クリック'}</div>
                    <div className="text-2xl font-semibold">{link.clicks ?? 0}</div>
                  </div>
                )}
                {(link.signups ?? null) !== null && (
                  <div className="border rounded-xl p-4 text-center">
                    <div className="text-xs text-gray-500">{t('invite.stat_signups') || '登録'}</div>
                    <div className="text-2xl font-semibold">{link.signups ?? 0}</div>
                  </div>
                )}
                {(link.conversions ?? null) !== null && (
                  <div className="border rounded-xl p-4 text-center">
                    <div className="text-xs text-gray-500">{t('invite.stat_conversions') || '成約'}</div>
                    <div className="text-2xl font-semibold">{link.conversions ?? 0}</div>
                  </div>
                )}
              </div>
            ) : null}

            {/* 日本語: 招待カード（自動生成・キャッシュ） */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold">{t('invite.card_title') || '招待カード'}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => { clearCardCache(link.ref); genCard(false); }}
                    className={classNames('px-3 py-1.5 rounded-lg text-sm border hover:bg-gray-100')}
                  >
                    {cardBusy ? (t('invite.card_generating') || '生成中…') : (t('invite.card_regenerate') || '再生成')}
                  </button>
                  <a
                    href={cardUrl || '#'}
                    download="melodis_invite.png"
                    className={classNames(
                      'px-3 py-1.5 rounded-lg text-sm',
                      cardUrl ? 'border hover:bg-gray-100' : 'border bg-gray-100 text-gray-400 cursor-not-allowed pointer-events-none'
                    )}
                  >
                    {t('invite.card_download') || 'ダウンロード'}
                  </a>
                  <button
                    onClick={shareCard}
                    disabled={!cardUrl}
                    className={classNames(
                      'px-3 py-1.5 rounded-lg text-sm',
                      cardUrl ? 'border hover:bg-gray-100' : 'border bg-gray-100 text-gray-400 cursor-not-allowed'
                    )}
                  >
                    {t('invite.share') || '共有'}
                  </button>
                </div>
              </div>

              <div className="border rounded-xl p-4 flex items-center justify-center min-h-[220px]">
                {cardBusy ? (
                  <p className="text-sm text-gray-600">{t('invite.card_generating') || '生成中…'}</p>
                ) : cardUrl ? (
                  <img src={cardUrl} alt="invite-card" className="rounded-xl border max-h-[360px] w-auto" />
                ) : (
                  <p className="text-sm text-gray-600">
                    {t('invite.card_hint') || '初回は自動生成されます。更新したい場合は「再生成」を押してください。'}
                  </p>
                )}
              </div>
            </section>
          </>
        )}

        <div className="flex justify-end gap-3 pt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-100"
          >
            {t('common.close') || '閉じる'}
          </button>
        </div>
        </div>{/* /p-6 */}
      </div>
      </div>
    </div>,
    document.body
  );
};

export default InviteDialog;
